// Morning e-mail «Η μέρα μου» (ideas track, idea 38). Every working day, at the time the advisor sets in the CRM
// (Settings `morningMail`, default 07:30 Europe/Nicosia, OFF by default), the platform e-mails a short digest of the
// day built from the data it already holds through sync: today's appointments, calls / follow-ups due or overdue,
// renewals in the next 30 days, birthdays and open premium arrears.
//
// Privacy by design: only counts, first names and times leave the platform by e-mail — never surnames, phone numbers,
// amounts, policy numbers, products, health or financial details. The day's «Ημερήσιο φύλλο» (Agenda → Daily sheet)
// is linked, not attached: it opens in the app, behind sign-in.
//
// Pure functions (unit-tested); the platform (packages/platform/src/morning.ts) schedules and sends.
import type { WorkspaceView } from './changeset.ts';
import { addDays, weekday, type Day } from './dates.ts';
import { openArrears } from './premiums.ts';
import { renewalQueue } from './business.ts';
import { splitName } from './rules.ts';
import { getSetting } from './services.ts';
import type { Person, Task } from './schema.ts';

export interface MorningMailSettings {
  enabled: boolean;
  /** HH:MM, the advisor's local time (time zone below) */
  time: string;
  /** recipient; empty = the e-mail of the account that turned it on */
  to: string;
  /** IANA time zone (default Europe/Nicosia) */
  timeZone: string;
  /** language of the e-mail */
  lang: 'el' | 'en';
  /** skip Cyprus public holidays as well as weekends (default true) */
  skipHolidays: boolean;
  /** the CRM member who turned it on (the default recipient); set by the app */
  userId?: string | null;
}
export const MORNING_MAIL_KEY = 'morningMail';
export const DEFAULT_MORNING_MAIL: MorningMailSettings = { enabled: false, time: '07:30', to: '', timeZone: 'Europe/Nicosia', lang: 'el', skipHolidays: true, userId: null };
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]+\.[^\s@]{2,}$/;
export const isMailAddress = (s: unknown) => EMAIL_RE.test(String(s ?? '').trim());

/** the effective settings: anything missing or malformed falls back to the default (never throws) */
export function normalizeMorningMail(raw: unknown): MorningMailSettings {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const tz = typeof o.timeZone === 'string' && o.timeZone ? o.timeZone : DEFAULT_MORNING_MAIL.timeZone;
  let zoneOk = true;
  try { new Intl.DateTimeFormat('en', { timeZone: tz }); } catch { zoneOk = false; }
  return {
    enabled: o.enabled === true,
    time: typeof o.time === 'string' && TIME_RE.test(o.time) ? o.time : DEFAULT_MORNING_MAIL.time,
    to: isMailAddress(o.to) ? String(o.to).trim() : '',
    timeZone: zoneOk ? tz : DEFAULT_MORNING_MAIL.timeZone,
    lang: o.lang === 'en' ? 'en' : 'el',
    skipHolidays: o.skipHolidays !== false,
    userId: typeof o.userId === 'string' && o.userId ? o.userId.slice(0, 64) : null,
  };
}
export const morningMailSettings = (v: WorkspaceView): MorningMailSettings => normalizeMorningMail(getSetting<unknown>(v, MORNING_MAIL_KEY, null));

// --- working days ---------------------------------------------------------------------------------------------
/** Orthodox Easter Sunday (Gregorian date) — Meeus' Julian algorithm + 13 days (valid 1900–2099) */
export function orthodoxEaster(year: number): Day {
  const a = year % 4, b = year % 7, c = year % 19;
  const d = (19 * c + 15) % 30, e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31), day = ((d + e + 114) % 31) + 1;
  const julian = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return addDays(julian, 13);
}
/** Cyprus public holidays of a year (fixed and Easter-based) */
export function cyprusHolidays(year: number): Day[] {
  const y = String(year);
  const fixed = ['01-01', '01-06', '03-25', '04-01', '05-01', '08-15', '10-01', '10-28', '12-24', '12-25', '12-26'].map((md) => `${y}-${md}`);
  const e = orthodoxEaster(year);
  // Green Monday, Good Friday, Easter Monday, Easter Tuesday, Kataklysmos (Pentecost Monday)
  const moving = [-48, -2, 1, 2, 50].map((n) => addDays(e, n));
  return [...new Set([...fixed, ...moving])].sort();
}
/** Monday to Friday, and (unless switched off) not a Cyprus public holiday */
export function isWorkingDay(day: Day, opts: { skipHolidays?: boolean } = {}): boolean {
  const wd = weekday(day);
  if (wd === 0 || wd === 6) return false;
  return opts.skipHolidays === false || !cyprusHolidays(Number(day.slice(0, 4))).includes(day);
}

// --- the digest -----------------------------------------------------------------------------------------------
export interface DigestEntry { name: string; time?: string | null; overdue?: boolean }
export interface MorningDigest {
  day: Day;
  appointments: DigestEntry[];
  calls: DigestEntry[];
  overdue: number;
  renewals: DigestEntry[];
  birthdays: DigestEntry[];
  arrears: DigestEntry[];
}

/** first name only (never the surname): the person's first name, else the first word of the name */
export function firstNameOf(p: Pick<Person, 'firstName' | 'displayName'> | { name?: string | null } | null | undefined): string {
  if (!p) return '';
  const x = p as { firstName?: string | null; displayName?: string; name?: string | null };
  return (x.firstName?.trim() || splitName(x.displayName ?? x.name ?? '').firstName || '').slice(0, 40);
}
const CALL_KINDS = new Set(['call', 'followup', 'task', 'review', 'document', 'renewal', 'other', 'application']);

/** what the morning e-mail will say for `day` — names are first names; no amounts, products or contact details */
export function morningDigest(v: WorkspaceView, day: Day): MorningDigest {
  const who = (t: Task) => {
    const p = t.personId ? v.get('persons', t.personId) : undefined;
    if (p && !p.archivedAt) return firstNameOf(p);
    const q = t.prospectId ? v.get('prospects', t.prospectId) : undefined;
    return q ? firstNameOf({ name: q.name }) : '';
  };
  const open = v.rows('tasks').filter((t) => t.status === 'open' && t.dueDate);
  const appointments = open.filter((t) => t.kind === 'appointment' && t.dueDate === day)
    .sort((a, b) => (a.dueTime ?? '99').localeCompare(b.dueTime ?? '99'))
    .map((t) => ({ name: who(t), time: t.dueTime ? t.dueTime.slice(0, 5) : null }));
  const due = open.filter((t) => CALL_KINDS.has(t.kind) && t.dueDate! <= day).sort((a, b) => a.dueDate!.localeCompare(b.dueDate!));
  const calls = due.map((t) => ({ name: who(t), overdue: t.dueDate! < day }));
  const renewals = renewalQueue(v, day, { includeLife: true }).filter((r) => r.days >= 0 && r.days <= 30)
    .map((r) => ({ name: firstNameOf(v.get('persons', r.policy.personId)), time: null }));
  const md = day.slice(5);
  const birthdays = v.rows('persons').filter((p) => !p.archivedAt && p.stage !== 'dependent' && p.birthDate && p.birthDate.slice(5) === md).map((p) => ({ name: firstNameOf(p) }));
  const arrears = openArrears(v, day).map((x) => ({ name: firstNameOf(v.get('persons', x.arrear.personId)) }));
  return { day, appointments, calls, overdue: calls.filter((c) => c.overdue).length, renewals, birthdays, arrears };
}
export const digestIsEmpty = (d: MorningDigest) => !d.appointments.length && !d.calls.length && !d.renewals.length && !d.birthdays.length && !d.arrears.length;

const TEXT = {
  el: {
    subject: (date: string, n: number) => `Η μέρα μου · ${date} · ${n === 1 ? '1 ραντεβού' : `${n} ραντεβού`}`,
    hello: (name: string) => (name ? `Καλημέρα ${name},` : 'Καλημέρα,'),
    intro: 'αυτή είναι η μέρα σας σε μια ματιά (μόνο αριθμοί και μικρά ονόματα — οι λεπτομέρειες είναι στο CRM).',
    appointments: 'Ραντεβού σήμερα', calls: 'Κλήσεις και follow-up για σήμερα', overdue: (n: number) => `${n} εκπρόθεσμα`,
    renewals: 'Ανανεώσεις τις επόμενες 30 ημέρες', birthdays: 'Γενέθλια σήμερα', arrears: 'Ανοιχτές καθυστερήσεις ασφαλίστρων',
    none: 'κανένα', more: (n: number) => `και ${n} ακόμη`, nothing: 'Δεν υπάρχει τίποτα προγραμματισμένο για σήμερα.',
    sheet: 'Το «Ημερήσιο φύλλο» της ημέρας (δύο σελίδες A4, για εκτύπωση) ανοίγει στο CRM:',
    footer: 'Το e-mail αυτό στέλνεται τις εργάσιμες ημέρες επειδή το ενεργοποιήσατε στο AVRASTRO CRM (Ρυθμίσεις → Πρωινό e-mail). Για να το σταματήσετε, απενεργοποιήστε το εκεί.',
  },
  en: {
    subject: (date: string, n: number) => `My day · ${date} · ${n === 1 ? '1 appointment' : `${n} appointments`}`,
    hello: (name: string) => (name ? `Good morning ${name},` : 'Good morning,'),
    intro: 'here is your day at a glance (counts and first names only — the details are in the CRM).',
    appointments: 'Appointments today', calls: 'Calls and follow-ups for today', overdue: (n: number) => `${n} overdue`,
    renewals: 'Renewals in the next 30 days', birthdays: 'Birthdays today', arrears: 'Open premium arrears',
    none: 'none', more: (n: number) => `and ${n} more`, nothing: 'Nothing is scheduled for today.',
    sheet: 'The day\'s «Daily sheet» (two A4 pages, for printing) opens in the CRM:',
    footer: 'This e-mail is sent on working days because you turned it on in AVRASTRO CRM (Settings → Morning e-mail). To stop it, turn it off there.',
  },
} as const;

/** the e-mail (plain text). `dateLabel` is the day as the reader reads it (e.g. «Πέμπτη 15 Οκτωβρίου»). */
export function morningMailText(d: MorningDigest, o: { lang: 'el' | 'en'; dateLabel: string; advisorFirstName?: string; sheetUrl?: string | null; maxNames?: number }): { subject: string; text: string } {
  const L = TEXT[o.lang];
  const max = o.maxNames ?? 8;
  const names = (xs: DigestEntry[], withTime = false) => {
    if (!xs.length) return L.none;
    const shown = xs.slice(0, max).map((x) => (withTime && x.time ? `${x.time} ${x.name || '—'}` : x.name || '—'));
    return shown.join(' · ') + (xs.length > max ? ` · ${L.more(xs.length - max)}` : '');
  };
  // the same first name twice in a list (two policies of one client) counts once
  const uniq = (xs: DigestEntry[]) => { const seen = new Set<string>(); return xs.filter((x) => { const k = x.name.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; }); };
  const lines = [L.hello(o.advisorFirstName ?? ''), L.intro, ''];
  if (digestIsEmpty(d)) lines.push(L.nothing, '');
  else {
    lines.push(`■ ${L.appointments} (${d.appointments.length}): ${names(d.appointments, true)}`);
    lines.push(`■ ${L.calls} (${d.calls.length}${d.overdue ? `, ${L.overdue(d.overdue)}` : ''}): ${names(uniq(d.calls))}`);
    lines.push(`■ ${L.renewals} (${d.renewals.length}): ${names(uniq(d.renewals))}`);
    lines.push(`■ ${L.birthdays} (${d.birthdays.length}): ${names(d.birthdays)}`);
    lines.push(`■ ${L.arrears} (${d.arrears.length}): ${names(uniq(d.arrears))}`);
    lines.push('');
  }
  if (o.sheetUrl) lines.push(L.sheet, o.sheetUrl, '');
  lines.push('—', L.footer);
  return { subject: L.subject(o.dateLabel, d.appointments.length), text: lines.join('\n') };
}

/** the next day the e-mail would go out (today included when the time has not passed) — shown in Settings */
export function nextMorningMailDay(s: MorningMailSettings, today: Day, nowTime: string): Day | null {
  if (!s.enabled) return null;
  for (let i = 0; i < 14; i++) {
    const d = addDays(today, i);
    if (i === 0 && nowTime >= s.time) continue;
    if (isWorkingDay(d, { skipHolidays: s.skipHolidays })) return d;
  }
  return null;
}
